import { PostgrestError, PostgrestSingleResponse } from '@supabase/supabase-js';
import { supabase } from '../../../../supabaseConfig';
import { IOrganizacoes } from '../../../types/tables/IOrganizacoes';

/**
 * Serviço para gerenciamento de organizações
 * Gerencia escolas, empresas e outras entidades no sistema
 *
 * NOTA: Este serviço não usa isolamento multi-tenant pois gerencia as próprias organizações
 */
export class OrganizacoesService {
     protected client = this.getClient();
     protected tableName = 'organizacoes';

     private getClient() {
          
          return supabase;
     }

     /**
      * Busca todas as organizações ativas
      */
     async getAllActive(): Promise<PostgrestSingleResponse<IOrganizacoes[]>> {
          return await this.client.from('organizacoes').select('*').eq('ativo', true).is('deleted_at', null).order('nome', { ascending: true });
     }

     /**
      * Busca organização por ID
      */
     async getById(id: number): Promise<PostgrestSingleResponse<IOrganizacoes>> {
          return await this.client.from('organizacoes').select('*').eq('id', id).is('deleted_at', null).single();
     }

     /**
      * Busca organização por CNPJ
      */
     async getByCnpj(cnpj: string): Promise<PostgrestSingleResponse<IOrganizacoes>> {
          return await this.client.from('organizacoes').select('*').eq('cnpj', cnpj).is('deleted_at', null).single();
     }

     /**
      * Busca organização por email
      */
     async getByEmail(email: string): Promise<PostgrestSingleResponse<IOrganizacoes>> {
          return await this.client.from('organizacoes').select('*').eq('email', email).is('deleted_at', null).single();
     }

     /**
      * Cria uma nova organização
      */
     async create(data: Partial<IOrganizacoes>, userId?: number): Promise<PostgrestSingleResponse<IOrganizacoes>> {
          const organizationData: any = {
               ...data,
               ativo: true,
               created_at: new Date().toISOString(),
               ...(userId && { created_by: userId }),
          };

          return await this.client.from('organizacoes').insert(organizationData).select('*').single();
     }

     /**
      * Atualiza uma organização
      */
     async update(id: number, data: Partial<IOrganizacoes>, userId?: number): Promise<PostgrestSingleResponse<IOrganizacoes>> {
          const updateData = {
               ...data,
               updated_at: new Date().toISOString(),
               ...(userId && { updated_by: userId }),
          };

          return await this.client.from('organizacoes').update(updateData).eq('id', id).select('*').single();
     }

     /**
      * Ativa uma organização
      */
     async ativar(id: number, userId?: number): Promise<PostgrestSingleResponse<IOrganizacoes>> {
          return await this.update(id, { ativo: true }, userId);
     }

     /**
      * Desativa uma organização
      */
     async desativar(id: number, userId?: number): Promise<PostgrestSingleResponse<IOrganizacoes>> {
          return await this.update(id, { ativo: false }, userId);
     }

     /**
      * Exclusão lógica de uma organização
      */
     async softDelete(id: number, userId?: number): Promise<{ error: PostgrestError | null }> {
          const data: any = {
               deleted_at: new Date().toISOString(),
               ativo: false,
               ...(userId && { deleted_by: userId }),
          };

          const { error } = await this.client.from('organizacoes').update(data).eq('id', id);

          return { error };
     }

     /**
      * Configura uma nova organização com dados padrão
      */
     async setupNovaOrganizacao(
          organizationData: Partial<IOrganizacoes>,
          adminData: {
               nome: string;
               email: string;
               documento_numero: string;
          },
          userId?: number,
     ): Promise<{ organizacao: IOrganizacoes; success: boolean }> {
          // Criar organização
          const { data: organizacaoData, error: orgError } = await this.create(organizationData, userId);

          if (orgError || !organizacaoData) {
               throw new Error(`Erro ao criar organização: ${orgError?.message || 'Dados não retornados'}`);
          }

          // Garantir que temos um objeto único, não array
          const organizacao = (organizacaoData as any) as IOrganizacoes;

          try {
               // Importar serviços necessários
               const { tipoPessoaService } = await import('./TipoPessoaService');
               const { pessoasService } = await import('./PessoasService');

               // Criar tipos de pessoa padrão
               await tipoPessoaService.criarTiposPadrao(organizacao.id, userId);

               // Buscar tipo Administrador
               const { data: tipoAdminData } = await tipoPessoaService.getByNome(organizacao.id, 'Administrador');
               const tipoAdmin = (tipoAdminData as any);

               if (tipoAdmin) {
                    // Criar usuário administrador
                    await pessoasService.create(
                         {
                              nome: adminData.nome,
                              email: adminData.email,
                              documento_tipo: 'CPF',
                              documento_numero: adminData.documento_numero,
                              fk_id_tipo_pessoa: tipoAdmin.id,
                              ativo: true,
                         },
                         organizacao.id,
                         userId,
                    );
               }

               // Criar grupos de produtos padrão
               const { produtosGruposService } = await import('./ProdutosGruposService');
               await produtosGruposService.criarGruposPadrao(organizacao.id, userId);

               return { organizacao, success: true };
          } catch (setupError) {
               // Se houver erro no setup, remover organização criada
               await this.softDelete(organizacao.id, userId);
               throw new Error(`Erro no setup da organização: ${setupError}`);
          }
     }

     /**
      * Busca estatísticas da organização
      * @deprecated - Função com problemas de tipagem, precisa revisar
      */
     async getEstatisticas(id: number): Promise<any> {
          return await this.client.rpc('fn_obter_dados_dashboard_coordenador' as any, {
               p_organizacao_id: id,
          });
     }

     /**
      * Verifica se a organização pode ser excluída
      */
     async canDelete(id: number): Promise<{ canDelete: boolean; reason?: string }> {
          // Verificar se há pessoas vinculadas
          const { data: pessoas } = await this.client.from('pessoas').select('id').eq('fk_id_organizacao', id).is('deleted_at', null);

          if (pessoas && pessoas.length > 0) {
               return { canDelete: false, reason: 'Organização possui pessoas vinculadas' };
          }

          // Verificar se há vendas
          const { data: vendas } = await this.client.from('vendas').select('id').eq('fk_id_organizacao', id);

          if (vendas && vendas.length > 0) {
               return { canDelete: false, reason: 'Organização possui histórico de vendas' };
          }

          return { canDelete: true };
     }
}

export const organizacoesService = new OrganizacoesService();
